import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Business, Assignment, CalendarToday, AttachMoney, Settings, FolderOpen, PersonAdd, Security, EventAvailable, EmojiEvents, CheckCircle, Assessment, EventNote, AccountBalance, Paid, Apartment, RateReview, VideoCall, Groups, HowToReg, Email, School } from '@mui/icons-material';
import AppShell, { NavItem, NavSubItem, NavGroup, NavDivider } from '../components/layout/AppShell';
import ProjectManagement from '../components/ProjectManagement';
import ClientManagement from '../components/ClientManagement';
import EmployeeManagement from '../components/EmployeeManagement';
import TimesheetApproval from '../components/TimesheetApproval';
import Reports from '../components/Reports';
import ChangePassword from '../components/ChangePassword';
import AdminAttendanceLeave from '../components/AdminAttendanceLeave';
import RoleManagement from '../components/RoleManagement';
import HolidayManagement from '../components/HolidayManagement';
import GoalManagement from '../components/GoalManagement';
import EmployeeLeaveBalance from '../components/EmployeeLeaveBalance';
import SalaryReport from '../components/SalaryReport';
import AdminPayslips from '../components/AdminPayslips';
import EmployeeDetails from '../components/EmployeeDetails';
import TenantManagement from '../components/TenantManagement';
import CompanySettings from '../components/CompanySettings';
import { useAuth } from '../contexts/AuthContext';
import OnboardingReview from '../components/OnboardingReview';
import InterviewFeedback from '../components/InterviewFeedback';
import InterviewScheduling from '../components/InterviewScheduling';
import MyInterviewPanel from '../components/MyInterviewPanel';
import GoogleMeetAdmin from '../components/GoogleMeetAdmin';
import EmailSettingsAdmin from '../components/EmailSettingsAdmin';
import LiveKitAdmin from '../components/LiveKitAdmin';
import InternshipManagement from '../components/InternshipManagement';

interface AdminDashboardProps {
  mobileOpen?: boolean;
  onDrawerClose?: () => void;
  onDrawerToggle?: () => void;
}

const TIMESHEET_VIEWS = ['approval', 'reports'];
const LEAVE_VIEWS = ['attendance', 'balance', 'googlemeet', 'livekit', 'emailsettings'];
const PAYROLL_VIEWS = ['salary', 'payslips'];
const RECRUITMENT_VIEWS = ['interviewscheduling', 'mypanel', 'interviews'];

const AdminDashboard: React.FC<AdminDashboardProps> = ({ mobileOpen = false, onDrawerClose, onDrawerToggle }) => {
  const navigate = useNavigate();
  const { view } = useParams<{ view?: string }>();
  // Managing every company is a platform-level power; a company's own Admin lands on their
  // employees instead and never sees the company list.
  const { isPlatformAdmin } = useAuth();
  const defaultView = isPlatformAdmin ? 'tenants' : 'employees';
  const requestedView = view || defaultView;
  const currentView = requestedView === 'tenants' && !isPlatformAdmin ? defaultView : requestedView;
  // Groups start open when the URL (on first load or refresh) points at one of their
  // views, so the sidebar reveals where you are instead of always resetting to defaults.
  const [openOrg, setOpenOrg] = useState(true);
  const [openTimesheet, setOpenTimesheet] = useState(TIMESHEET_VIEWS.includes(currentView));
  const [openLeave, setOpenLeave] = useState(LEAVE_VIEWS.includes(currentView));
  const [openPayroll, setOpenPayroll] = useState(PAYROLL_VIEWS.includes(currentView));
  const [openRecruitment, setOpenRecruitment] = useState(RECRUITMENT_VIEWS.includes(currentView));

  // Selecting a nav item navigates to its own URL (so refresh/back-forward keep the
  // right page+sidebar state) and closes the mobile drawer — on desktop (permanent
  // drawer) onDrawerClose is a no-op since mobileOpen never becomes true there.
  const selectView = (view: string) => {
    navigate(`/dashboard/${view}`);
    onDrawerClose?.();
  };

  const renderContent = () => {
    switch (currentView) {
      case 'tenants': return <TenantManagement />;
      case 'company': return <CompanySettings />;
      case 'clients': return <ClientManagement />;
      case 'projects': return <ProjectManagement />;
      case 'employees': return <EmployeeManagement />;
      case 'empdetails': return <EmployeeDetails />;
      case 'onboarding': return <OnboardingReview />;
      case 'roles': return <RoleManagement />;
      case 'holidays': return <HolidayManagement />;
      case 'goals': return <GoalManagement />;
      case 'internships': return <InternshipManagement />;
      case 'approval': return <TimesheetApproval />;
      case 'reports': return <Reports />;
      case 'attendance': return <AdminAttendanceLeave />;
      case 'balance': return <EmployeeLeaveBalance />;
      case 'googlemeet': return <GoogleMeetAdmin />;
      case 'livekit': return <LiveKitAdmin />;
      case 'emailsettings': return <EmailSettingsAdmin />;
      case 'salary': return <SalaryReport />;
      case 'payslips': return <AdminPayslips />;
      case 'interviewscheduling': return <InterviewScheduling />;
      case 'mypanel': return <MyInterviewPanel />;
      case 'interviews': return <InterviewFeedback />;
      case 'settings': return <ChangePassword />;
      default: return isPlatformAdmin ? <TenantManagement /> : <EmployeeManagement />;
    }
  };

  const drawerContent = (
        <>
            {isPlatformAdmin && (
              <NavItem icon={<Apartment />} label="Companies" active={currentView === 'tenants'} onClick={() => selectView('tenants')} />
            )}
            <NavItem icon={<Business />} label="My Company" active={currentView === 'company'} onClick={() => selectView('company')} />

            <NavDivider />

            <NavGroup icon={<Business />} label="Organization" open={openOrg} onToggle={() => setOpenOrg(!openOrg)}>
                <NavSubItem icon={<Business />} label="Clients" active={currentView === 'clients'} onClick={() => selectView('clients')} />
                <NavSubItem icon={<FolderOpen />} label="Projects" active={currentView === 'projects'} onClick={() => selectView('projects')} />
                <NavSubItem icon={<PersonAdd />} label="Employees" active={currentView === 'employees'} onClick={() => selectView('employees')} />
                <NavSubItem icon={<PersonAdd />} label="Employee Details" active={currentView === 'empdetails'} onClick={() => selectView('empdetails')} />
                <NavSubItem icon={<PersonAdd />} label="Onboarding Forms" active={currentView === 'onboarding'} onClick={() => selectView('onboarding')} />
                <NavSubItem icon={<Security />} label="Roles" active={currentView === 'roles'} onClick={() => selectView('roles')} />
                <NavSubItem icon={<EventAvailable />} label="Holidays" active={currentView === 'holidays'} onClick={() => selectView('holidays')} />
                <NavSubItem icon={<EmojiEvents />} label="Goals" active={currentView === 'goals'} onClick={() => selectView('goals')} />
                <NavSubItem icon={<School />} label="Internships" active={currentView === 'internships'} onClick={() => selectView('internships')} />
              </NavGroup>

            <NavGroup icon={<Assignment />} label="Timesheet" open={openTimesheet} onToggle={() => setOpenTimesheet(!openTimesheet)}>
                <NavSubItem icon={<CheckCircle />} label="Approval" active={currentView === 'approval'} onClick={() => selectView('approval')} />
                <NavSubItem icon={<Assessment />} label="Reports" active={currentView === 'reports'} onClick={() => selectView('reports')} />
              </NavGroup>

            <NavGroup icon={<CalendarToday />} label="Attendance & Leave" open={openLeave} onToggle={() => setOpenLeave(!openLeave)}>
                <NavSubItem icon={<EventNote />} label="Attendance" active={currentView === 'attendance'} onClick={() => selectView('attendance')} />
                <NavSubItem icon={<AccountBalance />} label="Leave Balance" active={currentView === 'balance'} onClick={() => selectView('balance')} />
                <NavSubItem icon={<VideoCall />} label="Google Meet" active={currentView === 'googlemeet'} onClick={() => selectView('googlemeet')} />
                <NavSubItem icon={<Groups />} label="Virtual Office" active={currentView === 'livekit'} onClick={() => selectView('livekit')} />
                <NavSubItem icon={<Email />} label="Email Settings" active={currentView === 'emailsettings'} onClick={() => selectView('emailsettings')} />
              </NavGroup>

            <NavGroup icon={<AttachMoney />} label="Payroll" open={openPayroll} onToggle={() => setOpenPayroll(!openPayroll)}>
                <NavSubItem icon={<Paid />} label="Generate Payslips" active={currentView === 'salary'} onClick={() => selectView('salary')} />
                <NavSubItem icon={<Assessment />} label="Employee Payslips" active={currentView === 'payslips'} onClick={() => selectView('payslips')} />
              </NavGroup>

            <NavGroup icon={<RateReview />} label="Recruitment" open={openRecruitment} onToggle={() => setOpenRecruitment(!openRecruitment)}>
                <NavSubItem icon={<EventNote />} label="Interview Scheduling" active={currentView === 'interviewscheduling'} onClick={() => selectView('interviewscheduling')} />
                <NavSubItem icon={<HowToReg />} label="My Interview Panel" active={currentView === 'mypanel'} onClick={() => selectView('mypanel')} />
                <NavSubItem icon={<RateReview />} label="Interview Feedback" active={currentView === 'interviews'} onClick={() => selectView('interviews')} />
              </NavGroup>

            <NavDivider />
            <NavItem icon={<Settings />} label="Settings" active={currentView === 'settings'} onClick={() => selectView('settings')} />
          </>
      );

  return (
    <AppShell
      panelTitle="Admin Panel"
      nav={drawerContent}
      mobileOpen={mobileOpen}
      onDrawerClose={onDrawerClose}
      onDrawerToggle={onDrawerToggle}
    >
      {renderContent()}
    </AppShell>
  );
};

export default AdminDashboard;
